import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isNil, isNull } from 'lodash-es';
import { map, Observable, of, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import * as fromMapSelectors from '../../../../store/selectors/map.selectors';
import * as fromPropertyProfileSelectors from '../../../../store/selectors/property-profile.selectors';
import { MapsService } from '../../../services/maps.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-map-view',
  standalone: false,
  templateUrl: './map-view.page.html'
})
export class MapViewPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private mapsService = inject(MapsService);
  private store = inject(Store);

  mapCenterCoordinates: google.maps.LatLngLiteral | null = null;
  mapDefaultZoom: number | null = null;
  mapInstance: google.maps.Map | null = null;
  profileId: string | null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;
  showMarker = true;
  defaultBackHref: string | undefined;

  mapCenter$: Observable<google.maps.LatLngLiteral>;
  mapOptions$: Observable<google.maps.MapOptions>;
  markerOptions$: Observable<google.maps.MarkerOptions>;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'] ?? MainTabBasePathEnum.PropertySearch;
    this.profileId = this.activatedRoute.snapshot.paramMap.get('id');

    this.mapCenter$ = of(this.profileId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromPropertyProfileSelectors.selectPropertyProfileMapCenter(id)))),
      tap((mapCenter) => {
        this.mapCenterCoordinates = mapCenter;
      })
    );

    this.mapOptions$ = this.store.pipe(
      select(fromMapSelectors.selectOptions),
      tap((mapOptions) => {
        this.mapDefaultZoom = mapOptions.zoom ?? null;
      })
    );

    this.markerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home-special.png'
        }
      }))
    );

    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this.updateMapType();
    this.centerOnPin();
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  centerOnPin() {
    if (this.mapCenterCoordinates) {
      this.mapInstance?.setCenter(this.mapCenterCoordinates);
    }
    if (this.mapDefaultZoom) {
      this.mapInstance?.setZoom(this.mapDefaultZoom);
    }
  }

  togglePinVisibility() {
    this.showMarker = !this.showMarker;
  }
}
